import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMicrosoftConnectionCheck } from "../use-microsoft-connection-check";
import DocumentStorageTab from "./DocumentStorageTab";
import MicrosoftConnectTab from "./MicrosoftConnectTab";
import DocumentPermitCard from "./DocumentPermitCard";
import { CompanyEntity } from "types/Company";

interface StorageSectionProps {
  companyEntity: Pick<
    CompanyEntity,
    "id" | "storageProvider" | "microsoft" | "documentPermitEmail"
  >;
  /** Loads the company again; a save is done when that has finished. */
  onRefresh: () => Promise<unknown>;
}

/**
 * Where documents are kept. The OneDrive forms below the storage card matter
 * only to a company that uses OneDrive, so they stay behind a quiet link
 * unless documents already live there or a Microsoft app is already set up.
 * The link sits right under the storage card, so its "connect OneDrive first"
 * message has a next step.
 *
 * Saving the Microsoft app checks the connection, so a wrong key shows up at
 * once; the result is shown in the Microsoft card.
 */
export default function StorageSection({
  companyEntity,
  onRefresh,
}: StorageSectionProps) {
  const { t } = useTranslation();
  const [isOneDriveOpened, setIsOneDriveOpened] = useState(false);
  // Two checks, because they answer different questions: the button in the
  // storage card (OneDrive companies only) and the one after the Microsoft
  // app is saved, which also matters before documents are moved to OneDrive.
  const storageCardCheck = useMicrosoftConnectionCheck(companyEntity.id);
  const savedAppCheck = useMicrosoftConnectionCheck(companyEntity.id);
  // A new app makes an earlier verdict in the storage card stale, so that one
  // is checked again too — the two must not contradict each other.
  const checkAfterSave = {
    ...savedAppCheck,
    run: async () => {
      await Promise.all([
        savedAppCheck.run(),
        storageCardCheck.result ? storageCardCheck.run() : undefined,
      ]);
    },
  };

  const showOneDrive =
    companyEntity.storageProvider === "microsoft" ||
    Boolean(companyEntity.microsoft?.clientId) ||
    isOneDriveOpened;

  return (
    <div className="space-y-5">
      <DocumentStorageTab connectionCheck={storageCardCheck} />

      {showOneDrive ? (
        <>
          <MicrosoftConnectTab
            companyId={companyEntity.id}
            microsoft={companyEntity.microsoft}
            onRefresh={onRefresh}
            check={checkAfterSave}
          />
          <DocumentPermitCard
            companyId={companyEntity.id}
            documentPermitEmail={companyEntity.documentPermitEmail}
            onRefresh={onRefresh}
          />
        </>
      ) : (
        <button
          type="button"
          onClick={() => setIsOneDriveOpened(true)}
          className="text-sm font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
        >
          {t("settings_storage_connect_onedrive")}
        </button>
      )}
    </div>
  );
}
